import React, { useCallback, useMemo, useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAlerts } from "@hooks/useAlerts";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { useMaintenanceRecords, useTenantOptions } from "./hooks";
import CreateMaintenanceWindowModal from "./CreateMaintenanceWindowModal";
import EditMaintenanceRecordModal from "./EditMaintenanceRecordModal";
import { endFromDuration, formatMexicoCityWindow } from "./maintenanceTime";
import {
	groupRecordsByWindow,
	STATUS_FILTER_TABS,
	type RecordGroup,
} from "./maintenanceWindowsModel";
import { deleteMaintenanceRecord, describeRequestError } from "./requests/maintenanceWindowsApi";
import type { MaintenanceRecordRow, MaintenanceRecordStatusFilter } from "./types";

function windowLabel(record: Pick<MaintenanceRecordRow, "date" | "duration">): string {
	return (
		formatMexicoCityWindow(record.date, endFromDuration(record.date, record.duration)) ?? "—"
	);
}

/** The full text is a paragraph or more; the row shows two lines until asked. */
function DescriptionCell({ text }: { text: string }) {
	const [expanded, setExpanded] = useState(false);
	return (
		<div className="min-w-0">
			<p
				className={`whitespace-pre-line text-sm text-semantic-text-secondary ${
					expanded ? "" : "line-clamp-2"
				}`}
			>
				{text}
			</p>
			<button
				type="button"
				className="text-xs font-medium text-semantic-text-brand underline"
				aria-expanded={expanded}
				onClick={() => setExpanded((value) => !value)}
			>
				{expanded ? "Show less" : "Show more"}
			</button>
		</div>
	);
}

function RecordRow({
	record,
	onEdit,
	onDelete,
}: {
	record: MaintenanceRecordRow;
	onEdit: (record: MaintenanceRecordRow) => void;
	onDelete: (record: MaintenanceRecordRow) => void;
}) {
	return (
		<li className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-x-4 gap-y-2 py-3">
			<div className="min-w-0">
				<span className="block font-medium text-semantic-text-primary truncate">
					{record.customer?.name ?? `Tenant #${record.tenantId}`}
				</span>
				{record.customer?.chargebeeCustomerId && (
					<span className="block text-xs text-semantic-text-secondary">
						{record.customer.chargebeeCustomerId}
					</span>
				)}
			</div>
			<DescriptionCell text={record.description} />
			<div className="flex gap-3 text-sm md:justify-end">
				<button
					type="button"
					className="font-medium text-semantic-text-brand underline"
					onClick={() => onEdit(record)}
				>
					Edit
				</button>
				<button
					type="button"
					className="font-medium text-semantic-text-danger underline"
					onClick={() => onDelete(record)}
				>
					Delete
				</button>
			</div>
		</li>
	);
}

/**
 * Rows that look like one window, under one heading. The grouping is only
 * visual (see `groupRecordsByWindow`): actions stay per customer.
 */
function WindowGroup({
	group,
	onEdit,
	onDelete,
}: {
	group: RecordGroup;
	onEdit: (record: MaintenanceRecordRow) => void;
	onDelete: (record: MaintenanceRecordRow) => void;
}) {
	const customers = group.records.length;
	return (
		<section className="rounded-sm border border-semantic-border-primary px-4 py-3">
			<header className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
				<h2 className="text-base font-bold text-semantic-text-primary">
					{group.heading || "Maintenance"}
				</h2>
				<span className="text-sm tabular-nums text-semantic-text-primary">
					{windowLabel(group)} <span className="text-semantic-text-secondary">CDMX</span>
				</span>
				<span className="text-sm text-semantic-text-secondary">
					{formatDuration(group.duration)} · {customers} customer{customers === 1 ? "" : "s"}
				</span>
			</header>
			<ul className="list-none m-0 p-0 divide-y divide-semantic-border-primary">
				{group.records.map((record) => (
					<RecordRow key={record.id} record={record} onEdit={onEdit} onDelete={onDelete} />
				))}
			</ul>
		</section>
	);
}

function RecordsBody({
	groups,
	loading,
	error,
	onEdit,
	onDelete,
}: {
	groups: RecordGroup[];
	loading: boolean;
	error: string | null;
	onEdit: (record: MaintenanceRecordRow) => void;
	onDelete: (record: MaintenanceRecordRow) => void;
}) {
	if (loading && groups.length === 0) {
		return (
			<div className="space-y-4 py-2" aria-busy="true">
				{[0, 1, 2].map((index) => (
					<SkeletonLine key={index} h={48} decorative />
				))}
			</div>
		);
	}
	if (error && groups.length === 0) return <ErrorFetchingItems text={error} />;
	if (groups.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-semantic-text-secondary">
				No maintenance windows here.
			</p>
		);
	}
	return (
		<div className="flex flex-col gap-4">
			{error && (
				<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-2 text-sm text-semantic-text-danger">
					{error}
				</p>
			)}
			{groups.map((group) => (
				<WindowGroup key={group.key} group={group} onEdit={onEdit} onDelete={onDelete} />
			))}
		</div>
	);
}

/**
 * Admin Center → Maintenance windows (CTX-1294).
 *
 * Where Networking turns a maintenance notice into something customers see:
 * pick the affected customers and services, set the hours, and each customer
 * gets a maintenance record listing its own services, shown on its Overview
 * and under Outages → Maintenance. No email is sent from here.
 *
 * A window is stored as one row per customer with nothing linking them, so
 * edits and deletes act on one customer's row at a time.
 *
 * Access is decided upstream by `getMaintenanceWindowsAllowedTenantTypes()`,
 * shared by the menu and the route guard.
 */
const MaintenanceWindowsPage: React.FC = () => {
	const api = useAxiosAuth0();
	const { confirm, alert, Alert } = useAlerts();
	const [status, setStatus] = useState<MaintenanceRecordStatusFilter>("upcoming");
	const [creating, setCreating] = useState(false);
	const [editing, setEditing] = useState<MaintenanceRecordRow | null>(null);

	const records = useMaintenanceRecords(status);
	const tenants = useTenantOptions();
	const { refresh } = records;

	const groups = useMemo(() => groupRecordsByWindow(records.data), [records.data]);

	const handleDelete = useCallback(
		async (record: MaintenanceRecordRow) => {
			const customer = record.customer?.name ?? `tenant #${record.tenantId}`;
			const confirmed = await confirm({
				title: `Delete this maintenance for ${customer}?`,
				description:
					"The customer stops seeing it. Other customers of the same window keep theirs.",
				confirmLabel: "Yes, delete it",
				cancelLabel: "Keep it",
				variant: "warning",
			});
			if (!confirmed) return;
			try {
				await deleteMaintenanceRecord(api, record.id);
				await refresh();
			} catch (err) {
				await alert({
					title: "Couldn't delete the maintenance",
					description: describeRequestError(err),
					variant: "error",
				});
			}
		},
		[api, confirm, alert, refresh]
	);

	// There is no toast system in the dashboard; `useAlerts` is the standard
	// way to confirm an outcome (see `AuditLogDetailPage`).
	const handleCreated = useCallback(
		async (created: MaintenanceRecordRow[]) => {
			await refresh();
			await alert({
				title: "Maintenance window created",
				description: `${created.length} customer${
					created.length === 1 ? "" : "s"
				} will see it under Maintenance.`,
				variant: "success",
			});
		},
		[alert, refresh]
	);

	return (
		<div className="p-6 space-y-6">
			<header className="flex flex-wrap items-start gap-3">
				<div className="flex-1 min-w-[260px]">
					<h1 className="text-h3 font-bold">Maintenance windows</h1>
					<p className="text-semantic-text-secondary mt-1 max-w-[66ch]">
						Scheduled maintenance announced to customers. Each affected customer gets
						its own copy, listing only its services, and sees it under Maintenance.
					</p>
				</div>
				<GenericButton
					label="New maintenance window"
					variant="brand"
					size="md"
					onClick={() => setCreating(true)}
				/>
			</header>

			{/* Without the customer list the form cannot pick anyone; say so here
			    rather than letting the picker read as "no customers exist". */}
			{tenants.error && (
				<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-2 text-sm text-semantic-text-danger">
					Couldn&apos;t load the customer list: {tenants.error}
				</p>
			)}

			<GenericCard
				isCollapsible={false}
				className="flex flex-col"
				bodyContent={
					<div className="flex flex-col gap-3">
						<div className="my-3">
							<GenericTabs
								tabs={STATUS_FILTER_TABS}
								activeTab={status}
								onTabChange={(value) => setStatus(value as MaintenanceRecordStatusFilter)}
								fullWidth={false}
								size="sm"
								aria-label="Maintenance status"
							/>
						</div>
						<RecordsBody
							groups={groups}
							loading={records.loading}
							error={records.error}
							onEdit={setEditing}
							onDelete={(record) => {
								void handleDelete(record);
							}}
						/>
					</div>
				}
			/>

			<CreateMaintenanceWindowModal
				isOpen={creating}
				tenantOptions={tenants.data}
				tenantsLoading={tenants.loading}
				onClose={() => setCreating(false)}
				onCreated={(created) => {
					void handleCreated(created);
				}}
			/>

			<EditMaintenanceRecordModal
				record={editing}
				onClose={() => setEditing(null)}
				onSaved={() => {
					void refresh();
				}}
			/>

			{Alert}
		</div>
	);
};

export default MaintenanceWindowsPage;
